import {
  AlertTriangle,
  ArrowLeftRight,
  Banknote,
  Bell,
  BookOpen,
  Box,
  Boxes,
  Building,
  Building2,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  ChartColumn,
  ClipboardCheck,
  ClipboardClock,
  ClipboardList,
  ClipboardPen,
  Clock,
  Cog,
  Contact,
  DollarSign,
  FileBadge,
  FileCheck2,
  FileSignature,
  FileStack,
  FileText,
  FileUser,
  Folder,
  FolderKanban,
  Gauge,
  Gavel,
  HandCoins,
  Handshake,
  History,
  Inbox,
  KeyRound,
  Landmark,
  Layers,
  LifeBuoy,
  ListChecks,
  Mail,
  Network,
  NotebookPen,
  Package,
  PackageOpen,
  Package2,
  PackageCheck,
  Percent,
  Phone,
  Receipt,
  ReceiptText,
  RefreshCw,
  Repeat,
  RotateCcw,
  Ruler,
  Scale,
  ScanBox,
  ScrollText,
  Settings,
  Settings2,
  Shapes,
  ShieldAlert,
  ShieldCheck,
  ShoppingCart,
  Shuffle,
  Siren,
  SlidersHorizontal,
  Smartphone,
  Tag,
  Tags,
  Target,
  TrendingUp,
  Truck,
  Undo2,
  UserCheck,
  Users,
  Warehouse,
  Video,
  Wallet,
  Wrench,
  Bookmark,
  LayoutGrid,
} from "lucide-react";

import type { LucideIcon } from "lucide-react";
import type { OrgSettings } from "@/types/organization/settings";
import type { PermissionCode } from "@/types/authorization";

/**
 * What a nav entry needs before it is worth showing.
 *
 * `permission` is a capability code (an array means any one of them admits);
 * `module` admits anyone holding *anything* in that module, which is how a
 * whole section is gated. Both are **presentation only** — the server checks
 * again on every request, so this hides doors, it does not lock them.
 *
 * Not to be confused with `featureKey` / `settingKey`, which are org feature
 * flags and settings: whether the org bought the thing, not whether you may
 * do it.
 */
interface NavGate {
  permission?: PermissionCode | PermissionCode[];
  module?: string;
  anyModule?: string[];
}

export interface NavSubItem extends NavGate {
  title: string;
  url: string;
  icon?: LucideIcon;
  featureKey?: string;
  /** Hide unless this org setting is enabled (mirrors `featureKey`). */
  settingKey?: keyof OrgSettings;
}

export interface NavSubMenu extends NavGate {
  title: string;
  url: string;
  icon?: LucideIcon;
  featureKey?: string;
  /** Hide unless this org setting is enabled (mirrors `featureKey`). */
  settingKey?: keyof OrgSettings;
  items: (NavSubItem | NavSubMenu)[];
}

export interface NavItem extends NavGate {
  title: string;
  url: string;
  icon: LucideIcon;
  isActive?: boolean;
  featureKey?: string;
  /** Hide unless this org setting is enabled (mirrors `featureKey`). */
  settingKey?: keyof OrgSettings;
  items?: (NavSubItem | NavSubMenu)[];
  /**
   * Where the section links when its own gates pass but every child is
   * hidden. Without it such a section is dropped; with it the section stays
   * as a plain link, so an enabled module never silently vanishes.
   */
  emptyUrl?: string;
}

interface ProjectItem {
  name: string;
  url: string;
  icon: LucideIcon;
}

interface UserDataType {
  user: {
    name: string;
    email: string;
    avatar: string;
  };
  navMain: NavItem[];
  mainMenuItems: NavItem[];
  projects: ProjectItem[];
}

export const userData: UserDataType = {
  user: {
    name: "shadcn",
    email: "m@example.com",
    avatar: "/avatars/shadcn.jpg",
  },
  navMain: [
    // {
    //   title: "Task Management",
    //   url: "#",
    //   icon: SquareTerminal,
    //   isActive: true,
    //   items: [
    //     {
    //       title: "Check-in",
    //       url: "/user/check-in",
    //     },
    //     {
    //       title: "Check-out",
    //       url: "/user/check-out",
    //     },
    //   ],
    // },
  ],
  mainMenuItems: [
    {
      title: "User Dashboard",
      url: "/user",
      icon: Gauge,
    },
    {
      title: "Attendance",
      url: "/user/attendance",
      icon: CalendarClock,
      featureKey: "attendance",
      items: [
        {
          title: "Attendance",
          url: "/user/attendance",
          icon: CalendarClock,
        },
        {
          title: "Timesheets",
          url: "/user/attendance/timesheet",
          icon: ClipboardClock,
        },
        {
          title: "Overtime",
          url: "/user/overtime",
          icon: Clock,
          settingKey: "overtime_approval_required",
        },
        {
          title: "Shift Assignments",
          url: "/user/attendance/shifts",
          icon: UserCheck,
        },
        {
          title: "Attendance Adjustments",
          url: "/user/attendance/adjustments",
          icon: Settings2,
        },
      ],
    },
    {
      title: "My Tasks",
      url: "/user/my-tasks",
      icon: ClipboardPen,
      featureKey: "tasks",
    },
    {
      title: "My Projects",
      url: "/user/my-projects",
      icon: FolderKanban,
      featureKey: "projects",
    },
    {
      title: "Leave",
      url: "/user/leave",
      icon: NotebookPen,
      featureKey: "leave",
      items: [
        {
          title: "My Leaves",
          url: "/user/leave",
          icon: NotebookPen,
        },
        {
          title: "Team Leave",
          url: "/user/team-leave",
          icon: Users,
        },
      ],
    },
    {
      title: "Calendar",
      url: "/user/calendar",
      icon: CalendarDays,
      // Every tab draws the attendance grid (timesheets, shifts, holidays).
      featureKey: "attendance",
    },
    {
      title: "Organization",
      url: "/user/organization/documents",
      icon: Building2,
      featureKey: "documents",
      items: [
        {
          title: "Documents",
          url: "/user/organization/documents",
          icon: Folder,
        },
      ],
    },
    {
      title: "CRM",
      url: "/user/crm/accounts",
      icon: Handshake,
      featureKey: "crm",
      // Every CRM page needs a CRM permission, even for own records.
      emptyUrl: "/user/crm/no-access",
      items: [
        { title: "Accounts", url: "/user/crm/accounts", icon: Building2, permission: "crm.account.view" },
        { title: "Contacts", url: "/user/crm/contacts", icon: Contact, permission: "crm.contact.view" },
        { title: "Leads", url: "/user/crm/leads", icon: Target, permission: "crm.lead.view" },
        { title: "Opportunities", url: "/user/crm/opportunities", icon: TrendingUp, permission: "crm.opportunity.view" },
        { title: "Cases", url: "/user/crm/cases", icon: LifeBuoy, permission: "crm.case.view" },
        { title: "Teams", url: "/user/crm/teams", icon: Users, permission: "crm.team.view" },
        { title: "Reports", url: "/user/crm/reports", icon: ChartColumn, permission: "crm.report.view" },
        { title: "Calendar", url: "/user/crm/calendar", icon: CalendarDays, permission: "crm.activity.view" },
        {
          title: "Activities",
          url: "#",
          icon: CalendarClock,
          permission: "crm.activity.view",
          items: [
            { title: "Calls", url: "/user/crm/calls", icon: Phone },
            { title: "Meetings", url: "/user/crm/meetings", icon: Video },
            { title: "Emails", url: "/user/crm/emails", icon: Mail },
            { title: "Tasks", url: "/user/crm/tasks", icon: ClipboardPen },
          ],
        },
      ],
    },
    {
      title: "Inventory",
      url: "/user/inventory/requisitions",
      icon: Package,
      featureKey: "inventory",
      items: [
        {
          title: "My Requisitions",
          url: "/user/inventory/requisitions",
          icon: FileCheck2,
        },
        {
          title: "My Deployment Manifests",
          url: "/user/inventory/deployment-manifests",
          icon: Truck,
        },
      ],
    },
    {
      title: "Expenses",
      url: "/user/expense",
      icon: Wallet,
      featureKey: "expenses",
    },
    {
      title: "Notifications",
      url: "/user/notifications",
      icon: Bell,
    },
    {
      title: "My Activity",
      url: "/user/my-activity",
      icon: History,
      featureKey: "audit",
    },
  ],
  projects: [
    // {
    //   name: "Design Engineering",
    //   url: "#",
    //   icon: Frame,
    // },
    // {
    //   name: "Sales & Marketing",
    //   url: "#",
    //   icon: PieChart,
    // },
    // {
    //   name: "Travel",
    //   url: "#",
    //   icon: Map,
    // },
  ],
};

export const adminData: UserDataType = {
  user: {
    name: "shadcn",
    email: "m@example.com",
    avatar: "/avatars/shadcn.jpg",
  },
  navMain: [
    // {
    //   title: "Task Management",
    //   url: "#",
    //   icon: SquareTerminal,
    //   isActive: true,
    //   items: [
    //     {
    //       title: "Check-in",
    //       url: "#",
    //     },
    //     {
    //       title: "Check-out",
    //       url: "#",
    //     },
    //   ],
    // },
  ],
  mainMenuItems: [
    {
      title: "Dashboard",
      url: "/admin",
      icon: Gauge,
      // A cross-module overview, not its own section — only relevant when
      // at least one of the sections it summarizes is itself reachable. A
      // role scoped to a single operational module (Storekeeper → Inventory,
      // a CRM-only role, ...) has nothing here worth showing.
      anyModule: [
        "attendance",
        "leave",
        "task",
        "salary",
        "users",
        "organization",
      ],
    },
    {
      title: "Users",
      url: "/admin/users",
      permission: ["users.hr.view", "users.account.manage", "users.profile.view"],
      icon: Users,
    },
    {
      title: "Attendance",
      url: "/admin/attendance/shifts",
      module: "attendance",
      icon: CalendarClock,
      featureKey: "attendance",
      items: [
        {
          title: "Timesheets",
          url: "/admin/attendance/timesheets",
          icon: ClipboardClock,
          permission: "attendance.timesheet.view",
        },
        {
          title: "Overtime",
          url: "/admin/overtime/monitoring",
          icon: Clock,
          permission: "attendance.overtime.view",
          settingKey: "overtime_approval_required",
        },
        {
          title: "Shifts",
          url: "/admin/attendance/shifts",
          icon: CalendarRange,
          permission: "attendance.shift.manage",
        },
        {
          title: "Shift Assignments",
          url: "/admin/attendance/assignments",
          icon: UserCheck,
          permission: "attendance.assignment.view",
        },
        {
          title: "Shift Policy",
          url: "/admin/attendance/shift-policy",
          icon: ScrollText,
          permission: "attendance.shift.manage",
        },
        {
          title: "Attendance Adjustments",
          url: "/admin/attendance/adjustments",
          icon: Settings2,
          permission: "attendance.timesheet.adjust",
        },
      ],
    },
    {
      title: "Reports",
      url: "/admin/reports",
      permission: [
        "attendance.timesheet.view",
        "leave.request.view",
        "attendance.overtime.view",
      ],
      icon: ChartColumn,
    },
    {
      title: "Leave",
      url: "/admin/leave/requests",
      module: "leave",
      icon: NotebookPen,
      featureKey: "leave",
      items: [
        {
          title: "Leave Requests",
          url: "/admin/leave/requests",
          icon: ClipboardCheck,
          permission: "leave.request.view",
        },
        {
          title: "Leave Types",
          url: "/admin/leave/types",
          icon: Shapes,
          permission: "leave.type.manage",
        },
        {
          title: "Leave Balances",
          url: "/admin/leave/balances",
          icon: Scale,
          permission: "leave.balance.view",
        },
        {
          title: "Accrual Rules",
          url: "/admin/leave/accrual-rules",
          icon: Gavel,
          permission: "leave.type.manage",
        },
        {
          title: "Accrual Logs",
          url: "/admin/leave/accrual-logs",
          icon: History,
          permission: "leave.balance.view",
        },
        {
          title: "Leave Policy",
          url: "/admin/leave/policy",
          icon: Settings2,
          permission: "leave.type.manage",
        },
        // {
        //   title: "Leave History",
        //   url: "/admin/leave-history",
        // },
      ],
    },
    {
      title: "Payroll",
      url: "/admin/payroll/payslips",
      module: "salary",
      icon: DollarSign,
      featureKey: "salaries",
      items: [
        {
          title: "Payslips",
          url: "/admin/payroll/payslips",
          icon: ReceiptText,
          permission: "salary.payslip.view",
        },
        {
          title: "Salaries",
          url: "/admin/payroll/salaries",
          icon: Banknote,
          permission: "salary.salary.view",
        },
        {
          title: "Advances",
          url: "/admin/payroll/advances",
          icon: HandCoins,
          permission: "salary.advance.manage",
        },
        {
          title: "Tax Brackets",
          url: "/admin/payroll/tax-brackets",
          icon: Percent,
          permission: "salary.setting.manage",
        },
        {
          title: "Settings",
          url: "/admin/payroll/settings",
          icon: Settings,
          permission: "salary.setting.manage",
        },
      ],
    },

    {
      title: "Calendar",
      url: "/admin/calendar",
      permission: "attendance.holiday.manage",
      icon: CalendarDays,
    },
    {
      title: "Tasks",
      url: "/admin/tasks",
      permission: ["task.task.view", "task.task.manage"],
      icon: ClipboardPen,
      featureKey: "tasks",
    },
    {
      title: "Projects",
      url: "/admin/projects",
      permission: ["project.project.view", "project.project.manage"],
      icon: FolderKanban,
      featureKey: "projects",
    },
    {
      title: "Organization",
      url: "/admin/organization/branches",
      icon: Building2,
      items: [
        {
          title: "Branches",
          url: "/admin/organization/branches",
          icon: Building,
          permission: "organization.structure.manage",
        },
        {
          title: "Departments",
          url: "/admin/organization/departments",
          icon: Network,
          permission: "organization.structure.manage",
        },
        {
          title: "Fiscal Years",
          url: "/admin/organization/fiscal-years",
          icon: Landmark,
          permission: "organization.setting.manage",
        },
        {
          title: "Devices",
          url: "/admin/organization/devices",
          icon: Smartphone,
          permission: "attendance.policy.manage",
        },
        {
          title: "Assign Device User",
          url: "/admin/organization/device-employees",
          icon: UserCheck,
          permission: "attendance.policy.manage",
        },
        {
          title: "Roles & Permissions",
          url: "/admin/organization/roles",
          icon: KeyRound,
          permission: "users.role.manage",
        },
        {
          title: "Audit Log",
          url: "/admin/audit-log",
          icon: History,
          permission: "audit.log.view",
          featureKey: "audit",
        },
      ],
    },
    {
      title: "Documents",
      url: "#",
      module: "document",
      icon: Folder,
      featureKey: "documents",
      items: [
        {
          title: "Company Documents",
          url: "/admin/documents/company",
          icon: FileBadge,
          permission: "document.company.manage",
        },
        {
          title: "Employee Documents",
          url: "/admin/documents/employee",
          icon: FileUser,
          permission: "document.employee.view",
        },
      ],
    },
    {
      title: "CRM",
      url: "/admin/crm/accounts",
      module: "crm",
      icon: Handshake,
      featureKey: "crm",
      items: [
        // Per-entity codes, so a role can be given accounts without contacts.
        // `crm.record.view` implies both server-side, so a module-wide holder
        // still sees everything here.
        { title: "Accounts", url: "/admin/crm/accounts", icon: Building2, permission: "crm.account.view" },
        { title: "Contacts", url: "/admin/crm/contacts", icon: Contact, permission: "crm.contact.view" },
        { title: "Leads", url: "/admin/crm/leads", icon: Target, permission: "crm.lead.view" },
        {
          title: "Opportunities",
          url: "/admin/crm/opportunities",
          icon: TrendingUp, permission: "crm.opportunity.view",
        },
        { title: "Cases", url: "/admin/crm/cases", icon: LifeBuoy, permission: "crm.case.view" },
        { title: "Teams", url: "/admin/crm/teams", icon: Users, permission: "crm.team.view" },
        { title: "Reports", url: "/admin/crm/reports", icon: ChartColumn, permission: "crm.report.view" },
        { title: "Calendar", url: "/admin/crm/calendar", icon: CalendarDays, permission: "crm.activity.view" },
        {
          title: "Activities",
          url: "#",
          icon: CalendarClock,
          permission: "crm.activity.view",
          items: [
            { title: "Calls", url: "/admin/crm/calls", icon: Phone },
            { title: "Meetings", url: "/admin/crm/meetings", icon: Video },
            { title: "Emails", url: "/admin/crm/emails", icon: Mail },
            { title: "Tasks", url: "/admin/crm/tasks", icon: ClipboardPen },
          ],
        },
      ],
    },
    {
      title: "Inventory",
      url: "/admin/inventory",
      module: "inventory",
      icon: Package,
      featureKey: "inventory",
      items: [
        {
          title: "Dashboard",
          url: "/admin/inventory",
          icon: Gauge,
          permission: "inventory.stock.view",
        },
        {
          title: "Stock",
          url: "#",
          icon: Boxes,
          items: [
            {
              title: "Items",
              url: "/admin/inventory/items",
              icon: Package2,
              permission: "inventory.item.manage",
            },
            {
              title: "Warehouses",
              url: "/admin/inventory/warehouses",
              icon: Warehouse,
              permission: "inventory.warehouse.manage",
            },
            {
              title: "Stock Levels",
              url: "/admin/inventory/stock-levels",
              icon: Layers,
              permission: "inventory.stock.view",
            },
          ],
        },
        {
          title: "Purchasing",
          url: "#",
          icon: Receipt,
          items: [
            {
              title: "Vendors",
              url: "/admin/inventory/vendors",
              icon: Truck,
              permission: "inventory.purchasing.manage",
            },
            {
              title: "Purchase Orders",
              url: "/admin/inventory/purchase-orders",
              icon: FileText,
              permission: "inventory.purchasing.manage",
            },
            {
              title: "Bills",
              url: "/admin/inventory/bills",
              icon: ReceiptText,
              permission: "inventory.purchasing.manage",
            },
          ],
        },
        {
          title: "Sales",
          url: "#",
          icon: ClipboardList,
          items: [
            {
              title: "Quotes",
              url: "/admin/inventory/quotes",
              icon: FileSignature,
              permission: "inventory.sales.manage",
            },
            {
              title: "Sales Orders",
              url: "/admin/inventory/sales-orders",
              icon: ShoppingCart,
              permission: "inventory.sales.manage",
            },
            {
              title: "Delivery Orders",
              url: "/admin/inventory/delivery-orders",
              icon: PackageCheck,
              permission: "inventory.sales.manage",
            },
            {
              title: "Invoices",
              url: "/admin/inventory/invoices",
              icon: FileStack,
              permission: "inventory.sales.manage",
            },
          ],
        },
        {
          title: "Transfer",
          url: "#",
          icon: ArrowLeftRight,
          items: [
            {
              title: "Requisitions",
              url: "/admin/inventory/requisitions",
              icon: FileCheck2,
              // RequisitionViewSet.get_queryset (scope_inventory_queryset)
              // widens the admin list beyond the caller's own rows for any
              // of these three codes, not just approve/issue — a
              // stock.view holder can see (read-only) every requisition
              // here too.
              permission: [
                "inventory.requisition.approve",
                "inventory.requisition.issue",
                "inventory.stock.view",
              ],
            },
            {
              title: "Stock Transfers",
              url: "/admin/inventory/transfers",
              icon: Shuffle,
              permission: "inventory.transfer.manage",
            },
            {
              title: "Delivery Challans",
              url: "/admin/inventory/challans",
              icon: FileText,
              permission: "inventory.stock.view",
            },
          ],
        },
        {
          title: "Adjustments",
          url: "#",
          icon: SlidersHorizontal,
          items: [
            {
              title: "Transactions",
              url: "/admin/inventory/transactions",
              icon: History,
              permission: "inventory.stock.view",
            },
            {
              title: "Returns",
              url: "/admin/inventory/returns",
              icon: Undo2,
              permission: "inventory.stock.adjust",
            },
            {
              title: "Customer Returns",
              url: "/admin/inventory/customer-returns",
              icon: RotateCcw,
              permission: "inventory.sales.manage",
            },
            {
              title: "Damage Reports",
              url: "/admin/inventory/damages",
              icon: AlertTriangle,
              permission: "inventory.stock.adjust",
            },
            {
              title: "Adjustments",
              url: "/admin/inventory/adjustments",
              icon: Settings2,
              permission: "inventory.stock.adjust",
            },
          ],
        },
        {
          title: "Assets & Rentals",
          url: "#",
          icon: Wrench,
          items: [
            {
              title: "Asset Items",
              url: "/admin/inventory/asset-items",
              icon: Box,
              permission: "inventory.rental.manage",
            },
            {
              title: "Rental Contracts",
              url: "/admin/inventory/rental-contracts",
              icon: FileSignature,
              permission: "inventory.rental.manage",
            },
            {
              // The agreements we rent equipment IN under, from third-party
              // vendors -- the inbound mirror of the rent-OUT contracts above.
              title: "Inbound Rentals",
              url: "/admin/inventory/inbound-rental-agreements",
              icon: PackageOpen,
              permission: "inventory.rental.manage",
            },
            {
              title: "Deployment Manifests",
              url: "/admin/inventory/deployment-manifests",
              icon: Truck,
              permission: "inventory.rental.manage",
            },
          ],
        },
        {
          title: "Compliance",
          url: "#",
          icon: ShieldCheck,
          items: [
            {
              title: "Inspections",
              url: "/admin/inventory/inspections",
              icon: ClipboardList,
              items: [
                {
                  title: "Records",
                  url: "/admin/inventory/inspections",
                  icon: FileText,
                  permission: "inventory.stock.view",
                },
                {
                  title: "Checklists",
                  url: "/admin/inventory/inspection-checklists",
                  icon: ListChecks,
                  permission: "inventory.item.manage",
                },
                {
                  title: "Frequencies",
                  url: "/admin/inventory/inspection-frequencies",
                  icon: Repeat,
                  permission: "inventory.item.manage",
                },
              ],
            },
            {
              title: "Asset Returns",
              url: "/admin/inventory/asset-safety-inspections",
              permission: "inventory.stock.view",
              icon: ScanBox,
              items: [
                {
                  title: "Asset Safety",
                  url: "/admin/inventory/asset-safety-inspections",
                  icon: ShieldAlert,
                  permission: "inventory.stock.view",
                },
                {
                  title: "Asset Incidents",
                  url: "/admin/inventory/asset-incidents",
                  icon: Siren,
                  permission: "inventory.stock.view",
                },
              ],
            },
            {
              title: "Reports",
              url: "/admin/inventory/reports",
              icon: ChartColumn,
              permission: "inventory.report.view",
            },
          ],
        },
        {
          title: "Settings",
          url: "#",
          icon: Settings,
          items: [
            {
              title: "Configuration",
              url: "/admin/inventory/settings",
              icon: Cog,
              permission: "inventory.setting.manage",
            },
            {
              title: "Price Books",
              url: "/admin/inventory/price-books",
              icon: BookOpen,
              permission: "inventory.pricing.manage",
            },
            {
              title: "Item Prices",
              url: "/admin/inventory/item-prices",
              icon: Tag,
              permission: "inventory.pricing.manage",
            },
            {
              title: "Supplier Prices",
              url: "/admin/inventory/supplier-prices",
              icon: Tags,
              permission: "inventory.pricing.manage",
            },
            {
              title: "Labels",
              url: "/admin/inventory/labels",
              icon: Bookmark,
              permission: "inventory.item.manage",
            },
            {
              title: "Categories",
              url: "/admin/inventory/categories",
              icon: LayoutGrid,
              permission: "inventory.item.manage",
            },
            {
              title: "Units of Measure",
              url: "/admin/inventory/units",
              icon: Ruler,
              permission: "inventory.item.manage",
            },
            {
              title: "Unit Conversions",
              url: "/admin/inventory/unit-conversions",
              icon: RefreshCw,
              permission: "inventory.item.manage",
            },
          ],
        },
      ],
    },
    {
      title: "Expenses",
      url: "/admin/expense/reports",
      module: "expense",
      icon: Wallet,
      featureKey: "expenses",
      items: [
        {
          title: "Reports",
          url: "/admin/expense/reports",
          icon: ChartColumn,
          permission: "expense.report.view",
        },
        {
          title: "Categories",
          url: "/admin/expense/categories",
          icon: LayoutGrid,
          permission: "expense.category.manage",
        },
        {
          title: "Settings",
          url: "/admin/expense/settings",
          icon: Settings2,
          permission: "organization.setting.manage",
        },
      ],
    },
    {
      title: "Notifications",
      url: "/admin/notifications",
      icon: Bell,
      items: [
        { title: "Notifications", url: "/admin/notifications", icon: Bell },
        {
          title: "Notification Settings",
          url: "/admin/notification-settings",
          icon: Cog,
          permission: "notification.setting.manage",
        },
      ],
    },
  ],
  projects: [
    // {
    //   name: "Design Engineering",
    //   url: "#",
    //   icon: Frame,
    // },
    // {
    //   name: "Sales & Marketing",
    //   url: "#",
    //   icon: PieChart,
    // },
    // {
    //   name: "Travel",
    //   url: "#",
    //   icon: Map,
    // },
  ],
};

export const supervisorData: UserDataType = {
  user: {
    name: "shadcn",
    email: "m@example.com",
    avatar: "/avatars/shadcn.jpg",
  },
  navMain: [],
  mainMenuItems: [
    {
      title: "Supervisor Dashboard",
      url: "/supervisor",
      icon: Gauge,
    },
    {
      title: "Leave Approvals",
      url: "/supervisor/approvals",
      icon: Inbox,
      featureKey: "leave",
      settingKey: "leave_supervisor_approval_required",
    },
    {
      title: "All Leave Requests",
      url: "/supervisor/requests",
      icon: ClipboardList,
      featureKey: "leave",
      settingKey: "leave_supervisor_approval_required",
    },
    {
      title: "Overtime Approvals",
      url: "/supervisor/overtime-approvals",
      icon: Clock,
      featureKey: "attendance",
      // No settingKey: claims reach approvers even with overtime approval
      // switched off (an inflated claim still needs a decision), and the
      // backend serves this inbox either way (T-165).
    },
    {
      title: "All Overtime Requests",
      url: "/supervisor/overtime-requests",
      icon: ClipboardClock,
      featureKey: "attendance",
      settingKey: "overtime_approval_required",
    },
    {
      title: "My Team",
      url: "/supervisor/team",
      icon: Users,
    },
    {
      title: "My Team Timesheet",
      url: "/supervisor/team-timesheet",
      icon: CalendarClock,
      featureKey: "attendance",
    },
    {
      title: "Notifications",
      url: "/supervisor/notifications",
      icon: Bell,
    },
  ],
  projects: [],
};
